import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";
import {
  createPfCorrectionRun,
  type PfCorrectionRunCreateRequest,
  type PfCorrectionRunResponse,
} from "../services/pfCorrection";

/**
 * Power factor correction study mutation (EOS-11a, A32).
 *
 * Mirrors useBusbarEvidence:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on an unmounted
 *   component.
 * - The run is attached to the revision chosen in the top bar, read at submit time; with no
 *   project, or one without an open revision, nothing is sent and the run is unassigned.
 * - Every calculation is persisted as a run; the typed result and the run summary (with its
 *   load, fault and SLD links) are exposed exactly as parsed, INDETERMINATE included.
 * - A linked load run or fault run must belong to the revision the study is saved in; the
 *   backend says so (409) and the message reaches the page unchanged.
 */
export function usePfCorrection() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<PfCorrectionRunResponse, Error, PfCorrectionRunCreateRequest>({
    mutationKey: ["electrical", "pf-correction", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createPfCorrectionRun(payload, controller.signal, activeRevisionId() ?? undefined);
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: PfCorrectionRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UsePfCorrectionResult = ReturnType<typeof usePfCorrection>;
